import { useCallback, useEffect, useReducer, useRef } from 'react';
import {
  ReportPadError,
  applyOperations,
  deepEqual,
  type Dataset,
  type ErrorCode,
  type Issue,
  type ReportDefinition,
  type ReportDetail,
  type ReportOperation
} from '@tallyframe/core';
import { diffDefinitions } from './diff';

/**
 * The edit loop: optimistic apply, bounded undo, debounced save.
 *
 * The renderer emits operations and owns nothing else; this is where they are
 * applied, remembered and persisted. Three rules shape it:
 *
 *  - an operation that core rejects is never applied locally, so the screen can
 *    never show a definition the server would refuse;
 *  - a save that conflicts is never resolved by discarding work — the local
 *    definition stays exactly where it was and the person decides;
 *  - the definition the server returns is authoritative once it lands, because
 *    it has been through `validateDefinition` and carries the new revision.
 */

/** How the engine saves and reloads; the app passes `useReportPersistence(ws)`.
 *  This, not an import, is how the engine reaches the server, so it carries
 *  no dependency on the app's API client, router or UI. */
export interface ReportPersistence {
  save(reportId: string, revision: number, operations: ReportOperation[]): Promise<ReportDetail>;
  load(reportId: string): Promise<ReportDetail>;
}

/** A failed save, read structurally from whatever `save` threw (an ApiError has these fields). */
export interface SaveFailure {
  code: ErrorCode;
  message: string;
  issues: Issue[];
}

/**
 * Reads a thrown value as a SaveFailure without importing the app's error
 * class. A value that carries a string `code` (an ApiError, say) is kept as
 * it is, so the screen can still recognise it. Anything else becomes an
 * INTERNAL `Error` — an Error rather than a plain object, because the error
 * banner prints a non-Error with `String(value)`.
 */
function toSaveFailure(error: unknown): SaveFailure {
  if (typeof error === 'object' && error !== null && typeof (error as { code?: unknown }).code === 'string') {
    return error as SaveFailure;
  }
  const message = error instanceof Error ? error.message : String(error);
  const issues: Issue[] = [];
  return Object.assign(new Error(message), { code: 'INTERNAL' as const, issues });
}

export type SaveState = 'saved' | 'unsaved' | 'saving' | 'conflict' | 'error';

/** Human text for each state. Also the accessible announcement. */
export const SAVE_STATE_LABEL: Record<SaveState, string> = {
  saved: 'Saved',
  unsaved: 'Unsaved changes',
  saving: 'Saving…',
  conflict: 'Conflict',
  error: "Couldn't save"
};

const SAVE_DEBOUNCE_MS = 700;

/**
 * Operations that represent a *finished* gesture.
 *
 * A debounce exists to coalesce a stream of related edits — `rename_report` and
 * `update_widget` arrive roughly one per keystroke, and nobody wants a PATCH per
 * character. But a drag-drop, an add or a delete is already complete when it
 * arrives: nothing more is coming, so sitting out the debounce does not save a
 * request, it just leaves the save indicator reading "Unsaved changes" for
 * three quarters of a second after the widget has visibly settled. That reads
 * as lag even though the main thread is idle the whole time.
 *
 * These flush on the next tick instead.
 */
const COMMITTED_OPS: ReadonlySet<ReportOperation['op']> = new Set([
  'set_layout',
  'add_widget',
  'remove_widget',
  'duplicate_widget',
  'set_print_options'
]);
/** Core caps a single `update_report` at 100 operations. */
const MAX_OPS_PER_REQUEST = 100;
/** How many definitions the undo stack keeps. */
const UNDO_LIMIT = 50;

interface Store {
  reportId: string;
  dataset: Dataset;
  definition: ReportDefinition;
  /** The newest definition the server has confirmed. Diff baseline for undo. */
  lastSaved: ReportDefinition;
  revision: number;
  pendingOps: ReportOperation[];
  undoStack: ReportDefinition[];
  redoStack: ReportDefinition[];
  saveState: SaveState;
  saveError: SaveFailure | null;
  /** An operation core refused. Shown and cleared by the next valid edit. */
  rejection: ReportPadError | null;
  inFlight: boolean;
  /** The batch the in-flight save sent. Null whenever `inFlight` is false,
   *  or when `adoptRestored` has fenced off the in-flight save, which will
   *  never move `lastSaved`. Undo and redo replay it on `lastSaved` to
   *  predict the baseline that save will leave behind. */
  inFlightBatch: ReportOperation[] | null;
  /** The promise of the save currently underway, including any chained
   *  follow-up save (chunking, or a resync after undo/redo). `flush()`
   *  returns this instead of starting a second request when one is already
   *  in flight. Cleared back to null once the chain it represents settles. */
  inFlightPromise: Promise<void> | null;
  timer: ReturnType<typeof setTimeout> | null;
  /** Bumped by undo/redo so a save that is already in flight knows its pending
   *  queue no longer describes the difference it thought it did. */
  resyncCounter: number;
  /** Bumped by `adoptRestored` so a save already in flight can tell it moved
   *  the baseline out from under it and must not apply its response —
   *  neither the definition it landed on nor the failure it hit is still
   *  relevant to what is on screen. */
  epoch: number;
  disposed: boolean;
}

export interface ReportEditing {
  definition: ReportDefinition;
  revision: number;
  saveState: SaveState;
  saveError: SaveFailure | null;
  rejection: ReportPadError | null;
  canUndo: boolean;
  canRedo: boolean;
  hasUnsavedWork: boolean;
  applyOps(operations: ReportOperation[]): void;
  undo(): void;
  redo(): void;
  retrySave(): void;
  dismissRejection(): void;
  /** Sends any pending edits right away, bypassing the debounce. If a save is
   *  already in flight, joins it instead of starting a second one: the
   *  returned promise does not settle until that save — and any follow-up
   *  save it chains into, from chunking a large batch or from a resync after
   *  undo/redo — has settled too. When nothing is pending, or the state is
   *  already 'conflict', there is nothing to wait on and it resolves right
   *  away. Once it resolves, either nothing is pending and `saveState` is
   *  'saved', or the state is 'conflict'/'error' and the pending work is
   *  retained — so a caller, such as the restore flow deciding whether it is
   *  safe to proceed, can `await flush()` and then trust `saveState`. */
  flush(): Promise<void>;
  /** Replaces the local definition with the server's, keeping the in-progress
   *  one on the undo stack so nothing the person did is lost. */
  reloadLatest(): Promise<void>;
  /** Adopts a detail fetched elsewhere (a refresh, say) without disturbing
   *  local edits: only the revision baseline moves when nothing is pending. */
  adopt(detail: ReportDetail): void;
  /** Adopts a restored revision unconditionally: sets the definition, the
   *  save baseline and the revision from `detail`, empties pending operations
   *  and clears both the undo and redo stacks. Unlike `adopt`, this always
   *  moves the baseline — the person just confirmed discarding whatever was
   *  in progress by restoring an earlier version. */
  adoptRestored(detail: ReportDetail): void;
}

export interface UseReportEditingOptions {
  detail: ReportDetail;
  /** Called whenever the server hands back an authoritative `ReportDetail`. */
  onDetail(detail: ReportDetail): void;
  /** The only way the engine reaches the server. */
  persistence: ReportPersistence;
}

/** Whatever core threw, as a ReportPadError the rejection banner can show. */
function toReportPadError(error: unknown): ReportPadError {
  return ReportPadError.isReportPadError(error)
    ? error
    : new ReportPadError('INTERNAL', error instanceof Error ? error.message : String(error));
}

/**
 * Refuses an undo or redo target the server would refuse; null when it is fine.
 *
 * Undo and redo queue a diff from `lastSaved`, not the operations a person
 * made, so on a report whose stored layout already overlaps they can rebuild
 * a placement core now refuses. Checked against `lastSaved`, and while a save
 * is in flight also against the baseline that save will leave behind: the
 * queue is re-diffed from it once the save lands.
 */
function refusalFor(store: Store, target: ReportDefinition): ReportPadError | null {
  const check = (baseline: ReportDefinition): ReportPadError | null => {
    const diff = diffDefinitions(baseline, target);
    if (diff.length === 0) return null; // applyOperations refuses an empty list
    try {
      applyOperations(baseline, diff, store.dataset);
      return null;
    } catch (error) {
      return toReportPadError(error);
    }
  };
  const first = check(store.lastSaved);
  if (first || !store.inFlight || !store.inFlightBatch) return first;
  let predicted: ReportDefinition;
  try {
    predicted = applyOperations(store.lastSaved, store.inFlightBatch, store.dataset).definition;
  } catch {
    // Defensive: adoptRestored clears inFlightBatch when it fences a save off,
    // so a batch that no longer replays on lastSaved should not reach here.
    // If one does, it cannot describe a baseline the server will reach, so
    // the lastSaved verdict stands.
    return first;
  }
  return check(predicted);
}

/**
 * The refusal an undo or redo shows. Core's overlap message names widget ids
 * and grid cells, which suit an agent but not a person at the editor, and the
 * step stays refused until the report changes, so it points at History too.
 * Any other refusal keeps core's message and issues.
 */
function stepRefusal(error: ReportPadError, step: 'undo' | 'redo'): ReportPadError {
  // assertNoNewOverlaps in core words each overlap issue this way.
  const overlap = error.issues.some((issue) => issue.message.includes(' would overlap '));
  if (!overlap) return new ReportPadError(error.code, `Can't ${step} this step: ${error.message}`, { issues: error.issues });
  return new ReportPadError(
    error.code,
    `Can't ${step} this step: it would put two widgets on top of each other. Restore an earlier version from History instead.`,
    { issues: [], cause: error }
  );
}

export function useReportEditing(options: UseReportEditingOptions): ReportEditing {
  const { detail, onDetail, persistence } = options;
  const [, render] = useReducer((n: number) => n + 1, 0);

  const onDetailRef = useRef(onDetail);
  onDetailRef.current = onDetail;
  const persistenceRef = useRef(persistence);
  persistenceRef.current = persistence;

  const storeRef = useRef<Store | null>(null);
  if (storeRef.current === null || storeRef.current.reportId !== detail.report.id) {
    if (storeRef.current?.timer) clearTimeout(storeRef.current.timer);
    storeRef.current = {
      reportId: detail.report.id,
      dataset: detail.dataset,
      definition: detail.report.definition,
      lastSaved: detail.report.definition,
      revision: detail.report.revision,
      pendingOps: [],
      undoStack: [],
      redoStack: [],
      saveState: 'saved',
      saveError: null,
      rejection: null,
      inFlight: false,
      inFlightBatch: null,
      inFlightPromise: null,
      timer: null,
      resyncCounter: 0,
      epoch: 0,
      disposed: false
    };
  }
  const store = storeRef.current;

  useEffect(() => {
    store.disposed = false;
    return () => {
      store.disposed = true;
      if (store.timer) clearTimeout(store.timer);
    };
  }, [store]);

  // Indirection so `schedule`'s timer always calls the latest `flush` without
  // capturing a stale closure across renders.
  const flushRef = useRef<() => Promise<void>>(async () => {});

  const flush = useCallback((): Promise<void> => {
    if (store.timer) {
      clearTimeout(store.timer);
      store.timer = null;
    }
    if (store.disposed) return Promise.resolve();
    if (store.inFlight) {
      // A save is already underway. Join it instead of starting a second
      // one — the promise it returned covers any chained follow-up save too.
      // `inFlightPromise` is always set in the same step `inFlight` is, so
      // it is never null here.
      return store.inFlightPromise as Promise<void>;
    }
    if (store.saveState === 'conflict') return Promise.resolve();
    if (store.pendingOps.length === 0) return Promise.resolve();

    /** Sends one batch and, if there is still more once it lands — another
     *  chunk, or a resync after undo/redo — chains straight into the next
     *  batch and awaits it, so the promise `flush()` returns settles only
     *  once the whole chain has, not just the first request in it.
     *
     *  Declared fresh here inside `flush`, rather than reached through a ref
     *  every render reassigns: a ref would let a chained call — made after a
     *  report-id change swapped it over — run against a different store's
     *  `pendingOps` and `revision` than the chain it started as part of.
     *  Recursing by calling itself keeps the whole chain bound to the
     *  `store` this call to `flush` closed over. Explicit `Promise<void>` so
     *  the self-reference does not become a self-referential type. */
    const send = async (): Promise<void> => {
      const batch = store.pendingOps.slice(0, MAX_OPS_PER_REQUEST);
      const resyncAtSend = store.resyncCounter;
      // Captured so a restore landing mid-flight (`adoptRestored`) can be
      // told apart from the save that was already underway when it did.
      const epochAtSend = store.epoch;
      store.inFlight = true;
      store.inFlightBatch = batch;
      store.saveState = 'saving';
      store.saveError = null;
      render();

      try {
        const saved = await persistenceRef.current.save(store.reportId, store.revision, batch);
        store.inFlight = false;
        store.inFlightBatch = null;
        if (store.disposed) return;
        if (store.epoch !== epochAtSend) {
          // `adoptRestored` moved the baseline while this save was in
          // flight. The response describes a revision, definition and
          // pending queue that adoption already replaced; applying it now
          // would drag the store back to before the restore. `inFlight` just
          // flipped false above, so render so a caller reading
          // `hasUnsavedWork` right after sees it — and if edits landed after
          // the restore but before this stale response, they are still
          // sitting in `pendingOps` unsent: send them now, at the revision
          // `adoptRestored` adopted, so they are not stranded.
          render();
          if (store.pendingOps.length > 0) await send();
          return;
        }

        store.revision = saved.report.revision;
        store.lastSaved = saved.report.definition;

        if (store.resyncCounter !== resyncAtSend) {
          // An undo or redo landed mid-flight: the queue no longer describes the
          // gap between what the server holds and what is on screen.
          store.pendingOps = diffDefinitions(store.lastSaved, store.definition);
        } else {
          store.pendingOps = store.pendingOps.slice(batch.length);
        }

        if (store.pendingOps.length === 0) {
          // Nothing outstanding, so the server's copy is the definitive one.
          store.definition = saved.report.definition;
          store.saveState = 'saved';
        } else {
          store.saveState = 'unsaved';
        }
        onDetailRef.current(saved);
        render();
        // Awaited, not fired-and-forgotten: a caller joining this save via
        // `flush()`'s `inFlightPromise` needs this follow-up to be part of
        // what it wakes up from too.
        if (store.pendingOps.length > 0) await send();
      } catch (error) {
        store.inFlight = false;
        store.inFlightBatch = null;
        if (store.disposed) return;
        if (store.epoch !== epochAtSend) {
          // Same reasoning as the success branch above: this failure belongs
          // to a save `adoptRestored` already superseded, so it must not
          // flip `saveState` to 'conflict' or 'error' — but the render and
          // the follow-up send are still owed.
          render();
          if (store.pendingOps.length > 0) await send();
          return;
        }
        const failure = toSaveFailure(error);
        store.saveError = failure;
        // Nothing is discarded in either branch: the pending operations and the
        // local definition survive so the work is still on screen and still undoable.
        store.saveState = failure.code === 'REVISION_CONFLICT' ? 'conflict' : 'error';
        render();
      }
    };

    const promise = send().finally(() => {
      if (store.inFlightPromise === promise) store.inFlightPromise = null;
    });
    store.inFlightPromise = promise;
    return promise;
  }, [store]);

  flushRef.current = flush;

  const schedule = useCallback(
    (delayMs: number = SAVE_DEBOUNCE_MS) => {
      if (store.timer) clearTimeout(store.timer);
      // Even the immediate path goes through a timer rather than calling flush
      // inline, so a save never starts while we are still mid-render.
      store.timer = setTimeout(() => {
        store.timer = null;
        void flushRef.current();
      }, delayMs);
    },
    [store]
  );

  const pushHistory = useCallback(
    (previous: ReportDefinition) => {
      store.undoStack.push(previous);
      if (store.undoStack.length > UNDO_LIMIT) store.undoStack.shift();
      store.redoStack.length = 0;
    },
    [store]
  );

  const applyOps = useCallback(
    (operations: ReportOperation[]) => {
      if (operations.length === 0) return;
      let next: ReportDefinition;
      try {
        next = applyOperations(store.definition, operations, store.dataset).definition;
      } catch (error) {
        // Rejected locally means never shown and never sent. The person sees
        // why, and the definition on screen is still one the server would accept.
        store.rejection = toReportPadError(error);
        render();
        return;
      }

      // An edit that changes nothing is not an edit. GridStack reports a drag
      // or resize that ends where it started as a full `set_layout`; queued,
      // it would save an empty revision, add a history entry nobody made and
      // push an undo step that undoes nothing.
      if (deepEqual(next, store.definition)) return;

      store.rejection = null;
      pushHistory(store.definition);
      store.definition = next;
      store.pendingOps = [...store.pendingOps, ...operations];
      if (store.saveState !== 'conflict') store.saveState = 'unsaved';
      render();
      if (store.saveState !== 'conflict') {
        const committed = operations.some((operation) => COMMITTED_OPS.has(operation.op));
        schedule(committed ? 0 : SAVE_DEBOUNCE_MS);
      }
    },
    [pushHistory, schedule, store]
  );

  const restore = useCallback(
    (target: ReportDefinition, from: 'undo' | 'redo') => {
      const counterpart = from === 'undo' ? store.redoStack : store.undoStack;
      counterpart.push(store.definition);
      if (counterpart.length > UNDO_LIMIT) counterpart.shift();
      store.definition = target;
      store.rejection = null;
      store.resyncCounter += 1;
      store.pendingOps = diffDefinitions(store.lastSaved, store.definition);
      if (store.saveState !== 'conflict') {
        store.saveState = store.pendingOps.length === 0 ? 'saved' : 'unsaved';
      }
      render();
      if (store.pendingOps.length > 0 && store.saveState !== 'conflict') schedule();
    },
    [schedule, store]
  );

  const undo = useCallback(() => {
    const previous = store.undoStack.at(-1);
    if (!previous) return;
    const refusal = refusalFor(store, previous);
    if (refusal) {
      // Like a refused edit: shown, never applied, never sent. The entry
      // stays on the stack, so the person can try again once it would pass.
      store.rejection = stepRefusal(refusal, 'undo');
      render();
      return;
    }
    store.undoStack.pop();
    restore(previous, 'undo');
  }, [restore, store]);

  const redo = useCallback(() => {
    const next = store.redoStack.at(-1);
    if (!next) return;
    const refusal = refusalFor(store, next);
    if (refusal) {
      store.rejection = stepRefusal(refusal, 'redo');
      render();
      return;
    }
    store.redoStack.pop();
    restore(next, 'redo');
  }, [restore, store]);

  const retrySave = useCallback(() => {
    if (store.saveState === 'conflict') return;
    store.saveError = null;
    void flush();
  }, [flush, store]);

  const dismissRejection = useCallback(() => {
    store.rejection = null;
    render();
  }, [store]);

  const reloadLatest = useCallback(async () => {
    const inProgress = store.definition;
    const fresh = await persistenceRef.current.load(store.reportId);
    if (store.disposed) return;
    // The person's work is not thrown away: it goes onto the undo stack, so one
    // press of Undo brings it back on top of the definition just loaded.
    store.undoStack.push(inProgress);
    if (store.undoStack.length > UNDO_LIMIT) store.undoStack.shift();
    store.redoStack.length = 0;
    store.definition = fresh.report.definition;
    store.lastSaved = fresh.report.definition;
    store.revision = fresh.report.revision;
    store.dataset = fresh.dataset;
    store.pendingOps = [];
    store.saveState = 'saved';
    store.saveError = null;
    store.resyncCounter += 1;
    onDetailRef.current(fresh);
    render();
  }, [store]);

  const adopt = useCallback(
    (fresh: ReportDetail) => {
      store.dataset = fresh.dataset;
      if (store.pendingOps.length === 0 && !store.inFlight && store.saveState !== 'conflict') {
        store.definition = fresh.report.definition;
        store.lastSaved = fresh.report.definition;
        store.revision = fresh.report.revision;
      }
      render();
    },
    [store]
  );

  const adoptRestored = useCallback(
    (fresh: ReportDetail) => {
      if (store.timer) {
        clearTimeout(store.timer);
        store.timer = null;
      }
      store.definition = fresh.report.definition;
      store.lastSaved = fresh.report.definition;
      store.revision = fresh.report.revision;
      store.dataset = fresh.dataset;
      store.pendingOps = [];
      store.undoStack.length = 0;
      store.redoStack.length = 0;
      store.saveState = 'saved';
      store.saveError = null;
      store.rejection = null;
      store.resyncCounter += 1;
      // Fences off a save already in flight: `send()` in `flush` checks this
      // against the epoch it captured when it sent, and drops its response
      // on the floor rather than applying it once it lands.
      store.epoch += 1;
      // `inFlight` stays true until that save settles, but it will never move
      // `lastSaved`, so undo and redo must not predict a baseline from it.
      store.inFlightBatch = null;
      onDetailRef.current(fresh);
      render();
    },
    [store]
  );

  return {
    definition: store.definition,
    revision: store.revision,
    saveState: store.saveState,
    saveError: store.saveError,
    rejection: store.rejection,
    canUndo: store.undoStack.length > 0,
    canRedo: store.redoStack.length > 0,
    hasUnsavedWork: store.pendingOps.length > 0 || store.inFlight,
    applyOps,
    undo,
    redo,
    retrySave,
    dismissRejection,
    flush,
    reloadLatest,
    adopt,
    adoptRestored
  };
}
